import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import localization from 'assets/localization/localization';
import DropDownInput from 'components/sales/Input/dropDownInput/dropDownInput';
import ConstantInput from 'components/sales/Input/constantInput/constantInput';
import Regex from 'services/utils/regex/Regex';
import 'react-toastify/dist/ReactToastify.css';
import { SectionHeader } from './common';
import { MILESTONES, TABLE_STYLES, BUTTON_STYLES } from './constants/invoiceConstants';
import {
    allocateWholeRupeeAmounts,
    parseMilestonePercentage,
    percentagesTotalIsHundred,
} from 'services/utils/helpers/invoiceValidators';

const MilestoneInvoiceModule = ({
    setInputData,
    inputData,
    handleCheckboxChange,
    handleDescriptionChange,
    lineData,
    handleAmountChange,
    isCheckboxDisabled,
    raBills,
    solutionStages,
    inputRentention,
    setInputRentention
}) => {

    const floorAmountFromPercentage = (percentage) => {
        const pct = Number(percentage);
        const total = Number(inputData?.total_amount);
        if (
            !Number.isFinite(pct) ||
            pct <= 0 ||
            !Number.isFinite(total) ||
            total <= 0
        ) {
            return '';
        }
        return (Math.floor((total * pct) / 100)).toFixed(2);
    };

    // When milestone + retention percentages total ~100%, allocate whole rupees
    // from full-precision percentages (preserves commercial splits like 695000/168000).
    const allocatedAmountByKey = (() => {
        const total = Number(inputData?.total_amount);
        if (!Number.isSafeInteger(total) || total <= 0) {
            return {};
        }

        const entries = [];
        MILESTONES.forEach((_, rowIndex) => {
            const row = lineData?.[0]?.[rowIndex];
            if (!row?.selected?.length) return;
            const percentage = parseMilestonePercentage(row.amount);
            if (percentage === null) return;
            entries.push({ key: `milestone-${rowIndex}`, percentage });
        });
        (inputRentention || []).forEach((retention, index) => {
            if (
                retention?.amount === '' ||
                retention?.amount === null ||
                retention?.amount === undefined
            ) {
                return;
            }
            const percentage = parseMilestonePercentage(retention.amount);
            if (percentage === null) return;
            entries.push({ key: `retention-${index}`, percentage });
        });

        if (entries.length === 0) {
            return {};
        }

        const percentages = entries.map((entry) => entry.percentage);
        if (!percentagesTotalIsHundred(percentages)) {
            return {};
        }

        const allocated = allocateWholeRupeeAmounts(total, percentages);
        if (!allocated) {
            return {};
        }

        return entries.reduce((map, entry, index) => {
            map[entry.key] = allocated[index].toFixed(2);
            return map;
        }, {});
    })();

    const calculateAmountFromPercentage = (key, percentage) => {
        if (allocatedAmountByKey[key] !== undefined) {
            return allocatedAmountByKey[key];
        }
        return floorAmountFromPercentage(percentage);
    };

    // Retention handlers
    const handleRetentionChange = (value, index, field) => {
        setInputRentention((prev = []) => {
            const updated = [...prev];

            // Ensure object exists
            if (!updated[index]) {
                updated[index] = {
                    description: '',
                    amount: '',
                    months: '',
                    stage_id: ''
                };
            }

            // Special handling for post_stage
            if (field === 'post_stage') {
                updated[index].stage_id = value.id;
                updated[index].post_stage_value = value.value;
            } else {
                updated[index][field] = value;
            }

            return updated;
        });
    };

    const handleAddRetentionClick = () => {
        const newRetention = {
            description: '',
            amount: '',
            months: '',
            stage_id: '',
        };
        setInputRentention([...(inputRentention || []), newRetention]);
    };

    const handleRemoveRetentionClick = (index) => {
        const updatedRetention = inputRentention?.filter((_, i) => i !== index) || [];
        setInputRentention(updatedRetention);
    };

    return (

        <>
            <div className="row">
                <div className="col-md-3" style={{ display: 'inline-flex' }}>
                    <span>{localization.raBills} </span>
                    <select
                        id="is_ra_bills"
                        style={{ width: "45px", height: "26px", marginLeft: "10px", marginBottom: "10px" }}
                        value={inputData.is_ra_bills}
                        onChange={(e) => {
                            setInputData({
                                ...inputData,
                                is_ra_bills: e.target.value
                            });
                        }}
                    >
                        {raBills.map((option) => (
                            <option key={option.id} value={option.id}>
                                {option.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Milestone Section */}
            <div className='row'>
                <div className='col-md-12'>
                    <SectionHeader title="Milestones" className="fw-bold" />
                </div>
            </div>
            <div className='row'>
                <div className='col-md-12'>
                    <table className='table' style={TABLE_STYLES.table}>
                        <tbody>
                            {
                                MILESTONES.map((label, rowIndex) => (
                                    <tr key={rowIndex}>
                                        <td style={TABLE_STYLES.cell}>
                                            <div style={{ display: 'inline-flex' }}>
                                                <input
                                                    type="checkbox"
                                                    style={{ width: "13px", height: "13px" }}
                                                    onChange={(e) => handleCheckboxChange(0, rowIndex, label.id, label.name, e.target.checked, true)}
                                                    checked={!!lineData[0]?.[rowIndex]?.selected?.includes(label.id)}
                                                    disabled={isCheckboxDisabled(label.id, 0, rowIndex)} />
                                                <span style={{ lineHeight: "14px" }}>&nbsp;{label.name}</span>
                                            </div>
                                        </td>
                                        <td style={TABLE_STYLES.cell}>
                                            <ConstantInput
                                                label={`% of Contract Amount`}
                                                type="number"
                                                min="0.01"
                                                max="100"
                                                step="0.01"
                                                isPrefix
                                                prefixText={'%'}
                                                regex={
                                                    !lineData[0]?.[rowIndex]?.amount ||
                                                    Regex.percentage.test(lineData[0]?.[rowIndex]?.amount)
                                                }
                                                value={lineData[0]?.[rowIndex]?.amount || ''}
                                                handleChange={(val) => {
                                                    handleAmountChange(0, rowIndex, val)
                                                }}
                                                required={lineData[0]?.[rowIndex]?.selected?.includes(label.id)}
                                                isStarRequired={lineData[0]?.[rowIndex]?.selected?.includes(label.id)}
                                                disabled={!lineData[0]?.[rowIndex]?.selected?.includes(label.id)}
                                            />
                                        </td>
                                        <td style={TABLE_STYLES.cell}>
                                            <ConstantInput
                                                label="Amount"
                                                type="text"
                                                className="indianrupee"
                                                isPrefix
                                                prefixText={'₹'}
                                                value={calculateAmountFromPercentage(
                                                    `milestone-${rowIndex}`,
                                                    lineData[0]?.[rowIndex]?.amount
                                                )}
                                                disabled
                                            />
                                        </td>
                                        <td style={TABLE_STYLES.cell}>
                                            <ConstantInput
                                                label={`Description`}
                                                type="text"
                                                required={lineData[0]?.[rowIndex]?.selected?.includes(label.id)}
                                                isStarRequired={lineData[0]?.[rowIndex]?.selected?.includes(label.id)}
                                                value={lineData[0]?.[rowIndex]?.description || ''}
                                                handleChange={(val) => {
                                                    handleDescriptionChange(0, rowIndex, val)
                                                }}
                                                disabled={!lineData[0]?.[rowIndex]?.selected?.includes(label.id)}
                                            />
                                        </td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Retention Section */}
            <div className='row'>
                <div className='col-md-12'>
                    <SectionHeader title="Retention Details" className="fw-bold" />
                </div>
            </div>

            <div className='row'>
                <div className='col-md-12'>
                    <table className='table' style={TABLE_STYLES.table}>
                        <tbody>
                            {(inputRentention || []).map((x, i) => (
                                <tr key={i}>
                                    <td style={{ ...TABLE_STYLES.cell, width: '15%' }}>
                                        <ConstantInput
                                            label={`% of Contract Amount`}
                                            type="number"
                                            min="0.01"
                                            max="100"
                                            step="0.01"
                                            className="indianrupee"
                                            isPrefix
                                            prefixText={'%'}
                                            regex={
                                                !x?.amount ||
                                                Regex.percentage.test(String(x.amount))
                                            }
                                            handleChange={(val) => handleRetentionChange(val, i, "amount")}
                                            value={x?.amount || ''}
                                        />
                                    </td>
                                    <td style={{ ...TABLE_STYLES.cell, width: '15%' }}>
                                        <ConstantInput
                                            label="Amount"
                                            type="text"
                                            className="indianrupee"
                                            isPrefix
                                            prefixText={'₹'}
                                            value={calculateAmountFromPercentage(
                                                `retention-${i}`,
                                                x?.amount
                                            )}
                                            disabled
                                        />
                                    </td>
                                    <td style={{ ...TABLE_STYLES.cell, width: '20%' }}>
                                        <ConstantInput
                                            label="Enter description"
                                            isStarRequired={x?.amount}
                                            value={x?.description || ''}
                                            handleChange={(val) => handleRetentionChange(val, i, "description")}
                                            disabled={!x?.amount}
                                        />
                                    </td>
                                    <td style={{ ...TABLE_STYLES.cell, width: '25%' }}>
                                        <ConstantInput
                                            label="Enter after months"
                                            type="number"
                                            handleChange={(val) => handleRetentionChange(val, i, "months")}
                                            value={x?.months || ''}
                                            min="0"
                                            isStarRequired={x?.amount}
                                            disabled={!x?.amount}
                                        />
                                    </td>
                                    <td style={{ ...TABLE_STYLES.cell, width: '20%' }}>
                                        <DropDownInput
                                            data={solutionStages || []}
                                            label="Select stage"
                                            getIdValue={(val) => handleRetentionChange(val, i, 'post_stage')}
                                            value={x?.post_stage_value || ''}
                                            isStarRequired={x?.amount}
                                            disabled={!x?.amount}
                                        />
                                    </td>
                                    <td style={{ ...TABLE_STYLES.cell, width: '10%' }}>
                                        <div style={{ display: 'flex', gap: '5px' }}>
                                            {(inputRentention || []).length > 1 && (
                                                <button
                                                    onClick={() => handleRemoveRetentionClick(i)}
                                                    className="btn btn-sm btn-danger"
                                                    style={BUTTON_STYLES.small}
                                                >
                                                    Remove Retention
                                                </button>
                                            )}
                                            {i === (inputRentention || []).length - 1 && (
                                                <button
                                                    className="btn btn-sm btn-primary"
                                                    onClick={handleAddRetentionClick}
                                                    style={BUTTON_STYLES.small}
                                                >
                                                    Add Retention
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className='row'>
                <div className='col-md-12'>
                    <hr />
                </div>
            </div>
            <div className="row align-items-center g-3">
                <div className="col-12 col-md-3">
                    <div>{localization.capex_convert_pi_ti}</div>
                    <div style={{ fontSize: "10px" }}>{localization.capex_convert_pi_ti_placeholder2}</div>
                </div>

                <div className="col-auto">
                    <div className="form-check">
                        <input
                            className="form-check-input radio-custom"
                            type="radio"
                            name="convert_pi_ti"
                            id="yes"
                            value="yes"
                            checked={inputData['convert_pi_ti'] === 'yes' ? true : false}
                            onChange={(e) => {
                                setInputData({
                                    ...inputData,
                                    convert_pi_ti: 'yes'
                                });
                            }} />
                        <label className="form-check-label" htmlFor="yes">
                            Yes
                        </label>
                    </div>
                </div>

                <div className="col-auto">
                    <div className="form-check">
                        <input
                            className="form-check-input radio-custom"
                            type="radio"
                            name="convert_pi_ti"
                            id="no"
                            value="no"
                            checked={inputData['convert_pi_ti'] === 'no' ? true : false}
                            onChange={(e) => {
                                setInputData({
                                    ...inputData,
                                    convert_pi_ti: 'no'
                                });
                            }} />
                        <label className="form-check-label" htmlFor="no">
                            No
                        </label>
                    </div>
                </div>
            </div>
        </>
    );
};

MilestoneInvoiceModule.propTypes = {
    handleValue: PropTypes.func,
};

export default MilestoneInvoiceModule;